import { useLocation } from 'react-router-dom';
import useRetainDefaultParams from '~/hooks/use-retain-default-params';
import useUIContext from '~/ui/use-ui-context';
import { Link } from '~/utils/navigation';

const QueryEncodedLinkFactory = Component => {
	const QueryEncodedLink = ({
		href: initialHref,
		children,
		activeMatch,
		activeExclude,
		activeClass = 'active',
		alignitemright, // consume this prop
		innerRef,
		dataAttribute,
		...rest
	}) => {
		const location = useLocation();
		const getLocationWithParams = useRetainDefaultParams();
		const href = getLocationWithParams(initialHref);
		const props = { ...rest, ref: innerRef, href };
		const { contexts } = useUIContext();
		const matchingContext = contexts.find(({ routesMatcher }) => routesMatcher(href));
		if (matchingContext) {
			const { queryEncoder } = matchingContext;
			const mainLocation = location.state?.mainLocation;
			props.href = queryEncoder({
				location: mainLocation ?? location,
				to: href
			});
		} else {
			props.href = contexts
				.map(({ queryDecoder, queryEncoder }) => ({
					decoded: queryDecoder(location),
					queryEncoder
				}))
				.filter(({ decoded }) => decoded)
				.reduce(
					(reducedHref, { decoded, queryEncoder }) =>
						queryEncoder(
							{
								location: {
									pathname: typeof reducedHref === 'object' ? reducedHref.pathname : reducedHref
								},
								to:
									typeof reducedHref === 'object'
										? {
												...reducedHref,
												pathname: decoded.pathname,
												search: decoded.search
											}
										: `${decoded.pathname}${decoded.search}`
							},
							location
						),
					href
				);
		}
		if (
			!activeMatch
				? location.pathname === href
				: location.pathname.startsWith(activeMatch) &&
					!(activeExclude && location.pathname.startsWith(activeExclude))
		) {
			if (props.className) props.className = props.className.concat(' ', activeClass);
		}
		if (dataAttribute) {
			props['data-at'] = dataAttribute;
		}

		return <Component {...props}>{children}</Component>;
	};
	return QueryEncodedLink;
};

const QueryEncodedLink = QueryEncodedLinkFactory(Link);

export { QueryEncodedLinkFactory };

export default QueryEncodedLink;
